import type { IncidentActivation, IncidentCall, IncidentDetail, IncidentStep } from "../../client-messages/incidents";

/**
 * One step of an incident's timeline, worded for support. `ticketNo` is the
 * activation the title names, which the step links to.
 */
export interface DescribedIncidentStep {
  readonly at: string;
  readonly title: string;
  readonly detail?: string;
  /** What the operator note records beyond the title: how a call was made, and how it went. */
  readonly facts?: readonly string[];
  /** The Patriot login of the operator who did it, where a note records one. */
  readonly operator?: string;
  readonly ticketNo?: string;
}

/**
 * One row of the timeline: a step, and the rail of every activation open at
 * it, one lane each, left to right. A lane is empty where no activation is
 * open in it.
 */
export interface IncidentTimelineRow {
  readonly step: DescribedIncidentStep;
  readonly rails: readonly (IncidentTimelineRail | undefined)[];
}

/** An activation's rail at one row of the timeline. */
export interface IncidentTimelineRail {
  readonly ticketNo: string;
  /** The step was made on this activation, rather than while it was open. */
  readonly on: boolean;
  /** The rail comes from the row above: the activation opened before this step. */
  readonly fromAbove: boolean;
  /** The rail goes on to the row below: the activation is open after this step. */
  readonly toBelow: boolean;
}

interface TimelineItem {
  readonly at: string;
  /** The activation the step was made on. */
  readonly ticketNo: string | undefined;
  /**
   * `within` is the only activation open at the step, when it was made on it
   * and an opened row above already names it.
   */
  readonly describe: (within: string | undefined) => DescribedIncidentStep;
}

interface ActivationSpan {
  readonly ticketNo: string;
  readonly first: number;
  readonly last: number;
  readonly stillOpen: boolean;
  readonly lane: number;
}

/**
 * Words the steps from `explainIncident` using only what the incident stored,
 * so the timeline cannot say anything the rule did not decide, then adds what
 * the operator notes say: who each call went to, who picked each activation up
 * and who completed it. Steps are in time order; an activation is a rail from
 * its first step to its last, so a call made outside it while it was open
 * shows inside its span, without its mark. Activations open at once take a
 * lane each, and a lane is reused once its activation closes. A step names its
 * activation unless that is the only one open at it and an opened row above
 * already names it. Support wording: it names tickets, notes and operators,
 * which a customer-facing view must not.
 */
export function describeIncidentTimeline(incident: IncidentDetail): IncidentTimelineRow[] {
  const items: TimelineItem[] = incident.steps.map((step) => ({
    at: step.at,
    ticketNo: ticketOf(step, incident),
    describe: (within) => describeStep(step, incident, within),
  }));
  const openedRows = new Set(
    incident.steps.flatMap((step) => (step.kind === "activationOpened" ? [step.ticketNo] : [])),
  );
  const ticketNos = new Set(items.flatMap((item) => (item.ticketNo !== undefined ? [item.ticketNo] : [])));
  for (const activation of incident.activations) {
    if (!ticketNos.has(activation.ticketNo)) continue;
    for (const pickUp of activation.pickUps ?? []) {
      const { ticketNo } = activation;
      items.push({
        at: pickUp.at,
        ticketNo,
        describe: (within) => ({
          at: pickUp.at,
          title: within === ticketNo ? "Picked up" : `Activation ${ticketNo} picked up`,
          ...(pickUp.operatorLogin !== undefined && { operator: pickUp.operatorLogin }),
          ...(within !== ticketNo && { ticketNo }),
        }),
      });
    }
  }
  // Stable, so steps at the same instant keep the rule's order.
  items.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));

  const spans = activationSpans(items, incident);
  const laneCount = Math.max(0, ...spans.map((span) => span.lane + 1));
  return items.map((item, index) => {
    const rails = Array.from({ length: laneCount }, (_, lane): IncidentTimelineRail | undefined => {
      const span = spans.find((s) => s.lane === lane && s.first <= index && index <= s.last);
      if (span === undefined) return undefined;
      return {
        ticketNo: span.ticketNo,
        on: item.ticketNo === span.ticketNo,
        fromAbove: index > span.first,
        toBelow: index < span.last || span.stillOpen,
      };
    });
    const open = rails.filter((rail) => rail !== undefined);
    const within =
      open.length === 1 && open[0]?.on === true && item.ticketNo !== undefined && openedRows.has(item.ticketNo) ?
        item.ticketNo
      : undefined;
    return { step: item.describe(within), rails };
  });
}

/**
 * Each activation's rows, from its first step to its last, or to the last row
 * while it is still open; then the first lane free when it opens. A ticket the
 * replica did not hold has no times, so it ends at its last step.
 */
function activationSpans(items: readonly TimelineItem[], incident: IncidentDetail): ActivationSpan[] {
  const bounds = new Map<string, { first: number; last: number }>();
  items.forEach((item, index) => {
    if (item.ticketNo === undefined) return;
    const bound = bounds.get(item.ticketNo);
    bounds.set(item.ticketNo, { first: bound?.first ?? index, last: index });
  });
  const laneEnds: number[] = [];
  return [...bounds].map(([ticketNo, { first, last }]) => {
    const activation = incident.activations.find((a) => a.ticketNo === ticketNo);
    const stillOpen = !incident.settled && activation?.openedAt !== undefined && activation.closedAt === undefined;
    const end = stillOpen ? items.length - 1 : last;
    const free = laneEnds.findIndex((laneEnd) => laneEnd < first);
    const lane = free === -1 ? laneEnds.length : free;
    laneEnds[lane] = end;
    return { ticketNo, first, last: end, stillOpen, lane };
  });
}

/** When an incident's timeline starts and ends, and its first call, whichever way it went. UTC ISO. */
export interface IncidentTimelineSpan {
  readonly startedAt: string;
  readonly firstCallAt: string | undefined;
  readonly endedAt: string;
}

/**
 * The span the timeline shows, which is wider than the rule's own
 * `startedAt`/`endedAt`: those cover only the calls and the activations called
 * on, while the timeline also holds the activations open near them and their
 * pick-ups. Settling comes after the incident ends, so it is left out.
 */
export function incidentTimelineSpan(incident: IncidentDetail): IncidentTimelineSpan {
  const instants = [
    incident.startedAt,
    incident.endedAt,
    ...incident.steps.flatMap((step) => (step.kind === "settled" ? [] : [step.at])),
    ...incident.activations.flatMap((activation) => activation.pickUps?.map((pickUp) => pickUp.at) ?? []),
  ].sort((a, b) => Date.parse(a) - Date.parse(b));
  const firstCall = incident.steps.find((step) => step.kind === "call");
  return {
    startedAt: instants[0] ?? incident.startedAt,
    firstCallAt: firstCall?.at,
    endedAt: instants.at(-1) ?? incident.endedAt,
  };
}

/** A call's title: who it went to where the note names them, else whether it was the police. */
export function callTitle(call: IncidentCall | undefined): string {
  const outbound = call?.direction !== "inbound";
  const contact = call?.note?.contact;
  const party =
    contact !== undefined ?
      outbound ? ` to ${contact}`
      : ` from ${contact}`
    : call?.police ?
      outbound ? " to the police"
      : " from the police"
    : "";
  return `${outbound ? "Outbound" : "Inbound"} call${party}`;
}

/**
 * Who the contact is on the client's call list, how the call was made and how
 * it went, each a line, from its operator note. The call-list line is left out
 * where no contact matched.
 */
export function callFacts(call: IncidentCall | undefined): string[] {
  const note = call?.note;
  if (note === undefined) return [];
  const dialled = [note.method, note.numberDialled].filter((part) => part !== undefined).join(" ");
  const onList = note.callListPosition !== undefined ? `#${String(note.callListPosition)} on the call list` : undefined;
  const contact = [note.contactType, onList].filter((part) => part !== undefined).join(", ");
  return [
    ...(contact !== "" ? [contact] : []),
    ...(dialled !== "" ? [dialled] : []),
    ...(note.autodial ? ["Logged by the autodialler"] : []),
    ...(note.outcome !== undefined ? [`“${note.outcome}”`] : ["No outcome written"]),
  ];
}

/** How a call made on an activation brought it into the incident. */
export function calledOn({ ticketNo }: IncidentActivation, incident: IncidentDetail): string {
  const count = incident.calls.filter((call) => call.ticketNo === ticketNo).length;
  return count > 1 ? `${String(count)} calls were made on it` : "A call was made on it";
}

const ticketOf = (step: IncidentStep, incident: IncidentDetail): string | undefined => {
  switch (step.kind) {
    case "call":
      return incident.calls.find((c) => c.noteId === step.noteId)?.ticketNo;
    case "activationOpened":
    case "activationClosed":
      return step.ticketNo;
    case "settled":
      return undefined;
  }
};

/**
 * `within` is the only activation open at the step, already named by an opened
 * row above, which the step then does not name.
 */
function describeStep(step: IncidentStep, incident: IncidentDetail, within?: string): DescribedIncidentStep {
  switch (step.kind) {
    case "call": {
      const call = incident.calls.find((c) => c.noteId === step.noteId);
      const outbound = call?.direction !== "inbound";
      const on =
        call?.ticketNo === undefined ? ", outside any activation"
        : call.ticketNo === within ? ""
        : ` on activation ${call.ticketNo}`;
      const facts = callFacts(call);
      return {
        at: step.at,
        title: `${callTitle(call)}${on}`,
        ...(step.madeGenuine ?
          { detail: "The first outbound call: this made it a genuine incident." }
        : !outbound && { detail: "Extends the incident; an inbound call never starts one." }),
        ...(facts.length > 0 && { facts }),
        ...(call?.note?.operatorLogin !== undefined && { operator: call.note.operatorLogin }),
        ...(call?.ticketNo !== undefined && call.ticketNo !== within && { ticketNo: call.ticketNo }),
      };
    }
    case "activationOpened": {
      const activation = incident.activations.find((a) => a.ticketNo === step.ticketNo);
      const details = [activation && whyIn(activation, incident), activation?.technicalOverridden && OVERRIDDEN].filter(
        (text): text is string => typeof text === "string",
      );
      return {
        at: step.at,
        title: `Activation ${step.ticketNo} opened`,
        ...(details.length > 0 && { detail: details.join(" ") }),
        ticketNo: step.ticketNo,
      };
    }
    case "activationClosed": {
      const activation = incident.activations.find((a) => a.ticketNo === step.ticketNo);
      const reason = activation?.reason;
      return {
        at: step.at,
        title: within === step.ticketNo ? "Closed" : `Activation ${step.ticketNo} closed`,
        ...(reason !== undefined && reason !== "" && { detail: `Cleared as ${reason}.` }),
        ...(activation?.completedBy !== undefined && { operator: activation.completedBy }),
        ...(within !== step.ticketNo && { ticketNo: step.ticketNo }),
      };
    }
    case "settled":
      return {
        at: step.at,
        title: "Settled",
        detail: "No further activity, and every activation closed.",
      };
  }
}

const OVERRIDDEN = "Counted here although its type is technical.";

const whyIn = (activation: IncidentActivation, incident: IncidentDetail): string => {
  const { link } = activation;
  if (link.kind === "called") return `${calledOn(activation, incident)}.`;
  const nearest =
    link.nearest.kind === "call" ?
      `the call on note ${link.nearest.noteId}`
    : `called activation ${link.nearest.ticketNo}`;
  const when =
    link.gapSeconds === 0 ?
      `Open at the same time as ${nearest}`
    : `Open within ${minutes(link.gapSeconds)} of ${nearest}`;
  return `${when}, so it is part of the incident.`;
};

const minutes = (seconds: number): string => `${String(Math.max(1, Math.round(seconds / 60)))} min`;
